import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CreditCard, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fcfa, fdate } from "@/lib/format";
import { uploadFile } from "@/lib/upload";
import { Loader, StatusBadge } from "@/components/common";

export function usePayMethods() {
  const [m, setM] = useState({ connectors: [], transfer_instructions: "" });
  useEffect(() => {
    api.get("/payments/methods").then(({ data }) => setM(data)).catch(() => {});
  }, []);
  return m;
}

export const PayButtons = ({ connectors, onPay, busy, testPrefix }) => (
  <div className="grid gap-2">
    {connectors.map((c) => (
      <button key={c.name} data-testid={`${testPrefix}-${c.name}-button`} className="mr-btn-primary w-full" onClick={() => onPay(c.name)} disabled={busy}>
        <CreditCard size={16} /> Payer avec {c.label}{c.simulated ? " (simulation)" : ""} <ArrowRight size={16} />
      </button>
    ))}
  </div>
);

export const TransferInfo = ({ text }) => text ? (
  <div className="border border-mr-border rounded-[3px] p-3 text-sm whitespace-pre-line" data-testid="transfer-instructions">
    <p className="text-xs uppercase tracking-wider text-mr-muted mb-1">Coordonnées pour le virement</p>{text}
  </div>
) : null;

function DeclareForm({ installments, onDone, instructions }) {
  const [method, setMethod] = useState("virement");
  const [reference, setReference] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const proof = await uploadFile("payment_proof", file);
      await api.post("/me/membership/declare", { method, reference, proof_file_id: proof, installments });
      toast.success("Paiement déclaré : il sera validé par le trésorier.");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-t border-mr-border pt-4 space-y-3" data-testid="declare-form">
      <p className="text-sm font-medium">Payer par virement : faites le virement puis déclarez-le avec la preuve.</p>
      <TransferInfo text={instructions} />
      <select data-testid="declare-method-select" className="mr-input" value={method} onChange={(e) => setMethod(e.target.value)}>
        <option value="virement">Virement bancaire</option>
        <option value="wave">Wave (hors application)</option>
      </select>
      <input data-testid="declare-reference-input" className="mr-input" placeholder="Référence de l'opération" value={reference} onChange={(e) => setReference(e.target.value)} />
      <label className="mr-btn-secondary w-full cursor-pointer"><Upload size={16} /> {file ? file.name : "Joindre la preuve"}
        <input data-testid="declare-proof-input" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
      </label>
      <button data-testid="declare-submit-button" className="mr-btn-secondary w-full" disabled={!file || busy} onClick={submit}>{busy ? "Envoi…" : "Déclarer mon paiement"}</button>
    </div>
  );
}

export function Contribution({ compact = false }) {
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [installments, setInstallments] = useState(false);
  const [busy, setBusy] = useState(false);
  const methods = usePayMethods();
  const load = () => api.get("/me/membership").then(({ data }) => setM(data)).catch(() => setM(false));
  useEffect(() => {
    load();
  }, []);
  if (m === null) return <Loader />;
  if (!m) return null;
  const nxt = m.next_installment;
  const choosing = !nxt && m.installment_preview.length > 0;
  const amount = nxt ? nxt.amount : installments ? m.installment_preview[0]?.amount : m.annual_fee;
  const pay = async (provider) => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/membership/checkout", { installments, provider });
      if (data.checkout_url.startsWith("http")) window.location.href = data.checkout_url;
      else nav(data.checkout_url);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const current = m.periods.find((p) => p.status === "active") || m.periods[0];
  return (
    <div className="space-y-4" data-testid="contribution-panel">
      {current?.end && <div className="mr-card flex items-center justify-between gap-3"><div><p className="text-xs uppercase tracking-wider text-mr-muted">Cotisation</p>
        <p className="font-medium mt-1" data-testid="contribution-due">Valable jusqu'au {fdate(current.end)}</p></div><StatusBadge status={current.status} /></div>}
      {m.can_pay && (
        <div className="mr-card space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div><p className="text-xs uppercase tracking-wider text-mr-muted">{nxt ? `Tranche ${nxt.rank} à régler` : installments ? "Première tranche" : current?.status === "active" ? "Renouvellement anticipé (période suivante)" : "Cotisation annuelle"}</p>
              <p className="font-display font-bold text-2xl tabular" data-testid="contribution-amount">{fcfa(amount)}</p>
              {nxt?.due_at && <p className="text-xs text-mr-muted tabular">Échéance : {fdate(nxt.due_at)}</p>}</div>
          </div>
          {choosing && (
            <div className="grid grid-cols-2 gap-2" data-testid="contribution-mode">
              <button data-testid="contribution-mode-full" onClick={() => setInstallments(false)} className={`border rounded-[3px] p-3 text-left text-sm ${!installments ? "border-mr-accent" : "border-mr-border"}`}>En une fois<br /><span className="tabular text-mr-muted">{fcfa(m.annual_fee)}</span></button>
              <button data-testid="contribution-mode-installments" onClick={() => setInstallments(true)} className={`border rounded-[3px] p-3 text-left text-sm ${installments ? "border-mr-accent" : "border-mr-border"}`}>En {m.installment_preview.length} fois<br /><span className="tabular text-mr-muted">{m.installment_preview.map((i) => fcfa(i.amount)).join(" + ")}</span></button>
            </div>
          )}
          {m.declared_pending ? <p className="text-sm text-mr-warning" data-testid="contribution-declared">Un paiement déclaré attend la validation du trésorier.</p> : (
            <>
              <PayButtons connectors={methods.connectors} onPay={pay} busy={busy} testPrefix="contribution-pay" />
              {!compact && <DeclareForm installments={installments} onDone={load} instructions={methods.transfer_instructions} />}
            </>
          )}
        </div>
      )}
      {!compact && m.periods.map((p) => (
        <div key={p.id} className="mr-card p-4 space-y-2" data-testid="membership-period-row">
          <div className="flex items-center justify-between gap-3 text-sm"><p className="tabular">{p.start ? `${fdate(p.start)} → ${fdate(p.end)}` : "Démarre au premier paiement validé"}</p><StatusBadge status={p.status} /></div>
          <p className="text-sm text-mr-muted tabular">{fcfa(p.amount)}{p.adjustment ? ` · ${p.adjustment.kind === "exoneration" ? "Exonération" : "Remise"} : ${p.adjustment.reason}` : ""}</p>
          {p.plan?.length > 1 && <ul className="text-xs space-y-1">{p.plan.map((i) => <li key={i.rank} className="flex justify-between tabular"><span>Tranche {i.rank}{i.due_at ? ` · ${fdate(i.due_at)}` : ""}</span><span>{fcfa(i.amount)} · {i.status === "paye" ? "payée" : "à payer"}</span></li>)}</ul>}
        </div>
      ))}
    </div>
  );
}
